import React, { forwardRef, memo, useEffect, useRef, useState } from 'react';
import { Tag, TagProps, Tooltip } from 'antd';
import { TooltipPlacement } from 'antd/es/tooltip';
import { TextWrap } from './style';

interface STextProps {
  text: string;
  /** 是否省略溢出文本并在悬停时提示，默认开启。 */
  ellipsis?: boolean;
  suffix?: string;
  style?: React.CSSProperties;
  onClick?: React.MouseEventHandler<HTMLSpanElement>;
  placement?: TooltipPlacement;
  tagMode?: boolean;
  color?: string;
  tagStyle?: React.CSSProperties;
  textColor?: string;
  tagIcon?: React.ReactNode
  tagProps?: TagProps;
}

interface TooltipTriggerProps extends React.HTMLAttributes<HTMLSpanElement> {
  anchorRef: React.RefObject<HTMLSpanElement>;
  hoverHandlersRef: React.MutableRefObject<Pick<React.HTMLAttributes<HTMLSpanElement>, 'onMouseEnter' | 'onMouseLeave'>>;
}

// Tooltip 按需挂载，但始终定位到未被替换的文本节点。
const TooltipTrigger = forwardRef<HTMLSpanElement, TooltipTriggerProps>(function TooltipTrigger({ anchorRef, hoverHandlersRef, onMouseEnter, onMouseLeave }, ref) {
  React.useImperativeHandle(ref, () => anchorRef.current!, [anchorRef]);
  // antd 会把原生 hover 处理器 clone 到 trigger 上，提交后捕获，避免渲染期写 ref。
  useEffect(() => {
    hoverHandlersRef.current = { onMouseEnter, onMouseLeave };
  });
  return null;
});

const SText: React.FC<STextProps> = ({ text, ellipsis = true, style, onClick, suffix, placement = 'top', tagMode = false, color, tagStyle, textColor, tagIcon, tagProps }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const textRef = useRef<HTMLSpanElement>(null);
  const hoverHandlersRef = useRef<Pick<React.HTMLAttributes<HTMLSpanElement>, 'onMouseEnter' | 'onMouseLeave'>>({});
  const enterTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const displayText = suffix && text ? text + suffix : text;

  // 文本或省略模式变化后，截断状态与已捕获的悬停处理器都会失效，关闭并复位。
  useEffect(() => {
    hoverHandlersRef.current = {};
    clearTimeout(enterTimerRef.current);
    enterTimerRef.current = undefined;
    setShowTooltip(false);
  }, [displayText, ellipsis]);

  useEffect(() => () => clearTimeout(enterTimerRef.current), []);

  const content = (
    <TextWrap
      ref={textRef}
      $ellipsis={ellipsis}
      onClick={onClick}
      onMouseEnter={ellipsis ? event => {
        const cell = event.currentTarget;
        if (cell.scrollWidth <= cell.clientWidth) return;
        if (showTooltip) {
          // 已打开时转发给 antd 的原生 enter，取消其延迟关闭。
          hoverHandlersRef.current.onMouseEnter?.(event);
          return;
        }
        // 对齐 antd 默认 100ms 进入延迟，避免鼠标扫过时连续弹出。
        if (enterTimerRef.current) return;
        enterTimerRef.current = setTimeout(() => {
          enterTimerRef.current = undefined;
          const el = textRef.current;
          if (el && el.scrollWidth > el.clientWidth) setShowTooltip(true);
        }, 100);
      } : undefined}
      onMouseLeave={event => {
        clearTimeout(enterTimerRef.current);
        enterTimerRef.current = undefined;
        if (!showTooltip) return;
        if (hoverHandlersRef.current.onMouseLeave) hoverHandlersRef.current.onMouseLeave(event);
        else setShowTooltip(false);
      }}
      style={{
        display: 'inline-block',
        maxWidth: '100%',
        verticalAlign: 'bottom',
        ...(tagMode ? { textAlign: 'center', fontSize: 14, lineHeight: 22 / 14 } : {}),
        ...style,
        ...(tagMode ? { color: textColor || 'unset', opacity: 0.8 } : {}),
      }}
    >
      {displayText}
      {showTooltip && <Tooltip
        title={text}
        open
        onOpenChange={open => {
          if (!open) {
            hoverHandlersRef.current = {};
            setShowTooltip(false);
          }
        }}
        placement={placement}
        destroyTooltipOnHide
        overlayInnerStyle={{ maxHeight: 200, maxWidth: 600, width: 'auto', overflow: 'auto' }}
      >
        <TooltipTrigger anchorRef={textRef} hoverHandlersRef={hoverHandlersRef} />
      </Tooltip>}
    </TextWrap>
  );

  return tagMode
    ? <Tag color={color} style={{ width: '100%', ...tagStyle }} icon={tagIcon} {...tagProps}>{content}</Tag>
    : content;
};

export default memo(SText);
